import React, { useMemo, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { User, Itinerary, ItineraryItem, ItineraryListItem } from '../types';
import { getIntelBullets, IntelBulletsResponse, checkShareStatus } from '../services/plansApi';
import { checkPublishOnVisit } from '../utils/publishWatch';
import { completedTripPrompt } from '../utils/publishOutcome';
import { updateItinerary, updateItineraryItem, checkinItineraryItem, favoriteItineraryItem, exportItineraryEmail, getSimilarItineraries, uploadActivityPhoto, deleteActivityPhoto, acknowledgeActivityPhotoShare, createInvitation, lockPlan, unlockPlan, voteItineraryItem, generateRecap, generateStory } from '../services/plannerApi';
import { ApiError } from '../services/apiBase';
import ItineraryEditModal from '../components/ItineraryEditModal';
import UnifiedMap from '../components/UnifiedMap';
import { itineraryItemsToMarkers } from '../utils/mapMarkers';
import { SkeletonItineraryCard, ConfirmDialog } from '../components/common';
import {
  TimelineView,
  ActivityActionSheet,
  DeleteDialog,
  ShareModal,
} from '../components/itinerary';
import ShareTripModal from '../components/plans/ShareTripModal';
import ShareSheet from '../components/plans/ShareSheet';
import { LineageLine } from '../components/itinerary/LineageLine';
import { trackEvent } from '../hooks/useAnalytics';
import { useItineraryPage } from '../hooks/useItineraryPage';
import { planChatEntryStorageKey, usePlanChat } from '../hooks/usePlanChat';
import PlanChatOverlay from '../components/chat/PlanChatOverlay';
import { ArrowLeft, MapPin, MoreVertical, Plane } from 'lucide-react';
import { Badge } from '../components/common/Badge';
import { BottomNav } from '../components/navigation';
import { useUserStore } from '../stores';
import { useUnsplashCredit } from '../hooks/useUnsplashCredit';
import { newUuid } from '../utils/uuid';
import './ItineraryView.css';
import { UnsplashCredit } from '../components/common/UnsplashCredit';

interface ItineraryViewProps {
  user: User | null;
}

function getGroupDisplayTitle(itinerary: { mode?: string; title: string; creator_name?: string }): string {
  if (itinerary.mode !== 'group') return itinerary.title;
  // If title already contains "&" or "crew" or "friends", user customized it
  if (/&|crew|friends|together/i.test(itinerary.title)) return itinerary.title;
  const name = itinerary.creator_name?.split(' ')[0] || 'You';
  return `${name} & friends \u2014 ${itinerary.title}`;
}

function computeStatLabel(itinerary: {
  days_until_departure?: number | null;
  current_trip_day?: number | null;
  is_active_trip?: boolean;
  lifecycle_state?: string;
}): { label: string; value: string } {
  if (itinerary.lifecycle_state === 'completed') {
    return { label: 'status', value: 'Done' };
  }
  if (itinerary.is_active_trip && itinerary.current_trip_day != null) {
    return { label: 'today', value: `Day ${itinerary.current_trip_day}` };
  }
  if (itinerary.days_until_departure != null && itinerary.days_until_departure > 0) {
    return { label: 'countdown', value: `${itinerary.days_until_departure}d` };
  }
  return { label: 'status', value: 'Planning' };
}

export async function saveItineraryContentPatch(
  itinerary: Itinerary,
  patch: Partial<Itinerary>,
): Promise<Itinerary> {
  return updateItinerary(itinerary.id, patch, itinerary.content_revision ?? 0);
}

/**
 * Generate a trip story, and REPORT when it does not happen.
 *
 * Story generation is the one action on this page with a failure the person can
 * actually act on. Sending `expected_revision` made `plan_version_conflict` a
 * designed outcome: the plan changed somewhere else — another tab, the phone — so
 * the revision held here is stale and the server refuses. §23 returns
 * `current_revision` precisely so the client can recover rather than guess.
 *
 * Silence costs more here than elsewhere. The provider call runs BEFORE the
 * revision compare, so a button that merely looks unresponsive spends a whole
 * story generation on every press, and the stale revision it kept guarantees the
 * next press fails the same way. Reloading is what breaks that loop: it is the
 * only thing that replaces the revision the conflict was about.
 *
 * Extracted rather than inlined at both buttons because a handler that reports is
 * only worth having if something binds that it reports.
 */
export async function generateStoryForPlan(
  itinerary: Itinerary,
  handlers: {
    apply: (story: string, contentRevision: number) => void;
    reload: (itineraryId: number) => Promise<boolean>;
    fail: (message: string) => void;
  },
): Promise<void> {
  try {
    const { story, content_revision } = await generateStory(
      itinerary.id, itinerary.content_revision ?? 0,
    );
    handlers.apply(story, content_revision);
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const reloaded = await handlers.reload(itinerary.id);
      handlers.fail(reloaded
        ? 'This plan changed somewhere else, so the story was not written. It has been reloaded — try again.'
        : 'This plan changed somewhere else, so the story was not written. Reopen the plan to get the latest version.');
      return;
    }
    handlers.fail('Could not write a trip story. Try again.');
  }
}

/**
 * Confirming an activity is the one edit whose failure is SILENT by nature:
 * the user's evidence that it worked is the lock appearing, and a failed
 * write leaves the old row on screen looking untouched. So there is no
 * quiet branch here — every outcome either shows the lock or says why not.
 *
 * RETURNS the failure message rather than routing it to the page's error
 * banner: the sheet deliberately stays open, and that banner renders UNDER
 * it (z-index 50 against the backdrop's 200, both `position: fixed` and
 * overlapping). Reporting into an occluded region is not reporting, so the
 * sheet renders this where the user is already looking.
 *
 * Extracted for the same reason `generateStoryForPlan` is: reaching the
 * action sheet through a render test means standing up the whole
 * `useItineraryPage` surface, and a mock that large fails for reasons
 * unrelated to what it claims.
 */
/**
 * Apply a completed lock write to the sheet that is still open — but ONLY if it
 * is still the same activity's sheet.
 *
 * The write is a round-trip and the sheet is dismissable while it is in flight.
 * Confirm A, close it, open B, and A's response arrives with B on screen: an
 * unguarded update paints B as confirmed, and B then offers to "Unconfirm"
 * something that was never confirmed. The user's own next tap writes the lie to
 * the server.
 */
export function applyLockToOpenSheet(
  prev: { item: ItineraryItem; dayNum: number } | null,
  itemId: number,
  confirmed: boolean,
): { item: ItineraryItem; dayNum: number } | null {
  if (!prev || prev.item.id !== itemId) return prev;
  return { ...prev, item: { ...prev.item, confirmed } };
}

/**
 * The "remove a stop" dialog's copy (Almanac S9b, the design's `confirmRemove`):
 * it names the stop and what goes with it, and says it cannot come back — the
 * item delete is a hard delete (`itinerary_item_delete`). Only what the stop
 * actually carries is named: a note when it has one, the photo when the user
 * added one (`photo_url`; the stock `image` is not theirs to lose). The day is
 * read from the date's own components, noon-anchored, never through UTC.
 */
export function removeStopCopy(
  item: Pick<ItineraryItem, 'name' | 'date' | 'notes' | 'photo_url'> | undefined,
): { title: string; message: string } {
  const name = item?.name?.trim();
  const title = name ? `Remove ${name}?` : 'Remove this stop?';
  let day = 'the plan';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(item?.date ?? '');
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
    if (!Number.isNaN(d.getTime())) day = d.toLocaleDateString('en-US', { weekday: 'long' });
  }
  const hasNote = Boolean(item?.notes?.trim());
  const hasPhoto = Boolean(item?.photo_url);
  const withWhat = hasNote && hasPhoto ? ', with its note and photo'
    : hasNote ? ', with its note'
      : hasPhoto ? ', with its photo'
        : '';
  return {
    title,
    message: `It comes off ${day}${withWhat}. You can ask chat for it again, but not get this one back.`,
  };
}

export async function toggleItemLock(
  itinerary: Itinerary,
  itemId: number,
  confirmed: boolean,
  handlers: {
    apply: (confirmed: boolean) => void;
    reload: (itineraryId: number) => Promise<boolean>;
  },
): Promise<string | null> {
  try {
    await updateItineraryItem(
      itinerary.id, itemId, { confirmed }, itinerary.content_revision ?? 0,
    );
    await handlers.reload(itinerary.id);
    // The sheet stays OPEN: locking is a state change the user should see
    // reflected, not a reason to lose their place.
    handlers.apply(confirmed);
    return null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      // Reload is what replaces the stale revision; without it every retry
      // repeats the same conflict. So if the reload ALSO failed, "try again"
      // is bad advice — the next attempt would send the same stale number.
      const reloaded = await handlers.reload(itinerary.id);
      return reloaded
        ? 'This plan changed somewhere else, so the lock was not saved. '
          + 'It has been reloaded — try again.'
        : 'This plan changed somewhere else, so the lock was not saved. '
          + 'Reopen the plan to get the latest version.';
    }
    return confirmed
      ? 'Could not confirm this activity. Try again.'
      : 'Could not unconfirm this activity. Try again.';
  }
}

const ItineraryView: React.FC<ItineraryViewProps> = ({ user }) => {
  const { anyPoolPhotoShown, report: reportPhotoTier } = useUnsplashCredit();
  const p = useItineraryPage();
  const planId = p.itinerary?.id ?? null;
  const { loadItinerary, navigate } = p;
  // The observed publish's on-next-visit leg, on the PAGE: a completed-trip
  // share (or a toggle whose poll gave up) leaves a "staged, not yet
  // observed" marker, and the plan may be opened without the share sheet
  // ever mounting. One look per visit; fires the funnel event once. Placed
  // before every early return — rules of hooks.
  const visitedId = p.itinerary?.id;
  const visitedSlug = p.itinerary?.slug;
  useEffect(() => {
    if (visitedId) void checkPublishOnVisit(visitedId, visitedSlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitedId]);
  // Called whenever the plan on screen may no longer match the server: after a
  // committed edit, and after a conflict. Either way the reader should not have
  // to close the chat to find out what their plan looks like now.
  const reloadPlan = React.useCallback(() => {
    if (planId) void loadItinerary(planId);
  }, [planId, loadItinerary]);
  const planChat = usePlanChat(
    planId, 'plan_edit', p.itinerary?.content_revision ?? null,
    null, reloadPlan,
  );
  const resetPlanChat = planChat.reset;
  const mapRef = React.useRef<HTMLDivElement>(null);
  const [showChat, setShowChat] = useState(false);
  const restoredEditEntryRef = React.useRef<number | null>(null);

  // Arrive with the chat open, from "Go to my plan" after a fork
  // (EXPLORE_PLAN_NOW_ALIGNMENT_PROPOSAL.md §3 Phase 1.4). Adopting a plan is the start of tailoring it, and the step that
  // used to sit between the two only pretended to tailor. Same `?chat=1`
  // convention Explore already uses, cleaned off the URL on arrival so closing
  // the chat and reloading does not reopen it. Other params (an `invite` token,
  // which `loadItinerary` reads) are preserved — this deletes one key, it does
  // not replace the query string.
  // No "already handled" latch. The param check IS the latch: the effect's own
  // navigation strips `chat`, and react-router memoizes `searchParams` on the
  // search string, so it cannot re-enter. A ref beside it added no behaviour
  // and could only subtract — a retained page receiving a second `?chat=1`
  // would be refused forever by the stale ref.
  const [searchParams] = useSearchParams();
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (searchParams.get('chat') !== '1') return;
    // NOT for an invited reader. The overlay is the `plan_edit` chat, and
    // "Edit with AI" is owner-gated everywhere else on this page; an invitee
    // arriving at `?invite=<token>&chat=1` would get an editor whose every
    // send the server refuses. The param is still stripped, so the URL ends up
    // the same either way — only the chat does not open.
    if (!searchParams.get('invite')) setShowChat(true);
    const rest = new URLSearchParams(searchParams);
    rest.delete('chat');
    const query = rest.toString();
    navigate(`${pathname}${query ? `?${query}` : ''}${hash}`, { replace: true });
  }, [searchParams, pathname, hash, navigate]);

  useEffect(() => {
    const itineraryId = p.itinerary?.id;
    if (!itineraryId || restoredEditEntryRef.current === itineraryId) return;
    restoredEditEntryRef.current = itineraryId;
    try {
      const raw = sessionStorage.getItem(
        planChatEntryStorageKey(useUserStore.getState().user?.id ?? null),
      );
      if (!raw) return;
      const restored = JSON.parse(raw);
      if (
        restored?.intent === 'edit_itinerary'
        && restored.itineraryId === itineraryId
        && typeof restored.entryId === 'string'
      ) {
        resetPlanChat(restored);
        setShowChat(true);
      }
    } catch { /* invalid recovery envelope */ }
  }, [p.itinerary?.id, resetPlanChat]);

  // Scroll itinerary chat to bottom when overlay opens
  useEffect(() => {
    if (!showChat) return;
    const t = setTimeout(() => p.chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    return () => clearTimeout(t);
  }, [showChat, p.chatEndRef]);
  const [intelBullets, setIntelBullets] = useState<IntelBulletsResponse | null>(null);
  const [actionSheetItem, setActionSheetItem] = useState<{ item: ItineraryItem; dayNum: number } | null>(null);
  const [showShareTripModal, setShowShareTripModal] = useState(false);
  const [shareEligible, setShareEligible] = useState(false);
  // The "Not now" key has yielded to a failed completed-trip share: the
  // prompt is AVAILABLE (the strip and its button), never opened by itself.
  const [shareRetry, setShareRetry] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [exportStatus, setExportStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [similarPlans, setSimilarPlans] = useState<ItineraryListItem[]>([]);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [editingStory, setEditingStory] = useState(false);
  const [storyText, setStoryText] = useState('');
  const [storyLoading, setStoryLoading] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [isLocking, setIsLocking] = useState(false);

  // Fetch similar plans
  useEffect(() => {
    if (!p.itinerary) return;
    let cancelled = false;
    getSimilarItineraries(p.itinerary.id)
      .then(data => { if (!cancelled) setSimilarPlans(data); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.itinerary?.id]);

  // The completed-trip share's WHOLE rule (increment 5, decision 6), one
  // definition in `completedTripPrompt`: a plan from a guide or another plan,
  // ended, not shared, three or more edits, not public (a public plan's
  // publish belongs to the sheet), not taken down by the owner; the "Not now"
  // key yields to a failed share — as AVAILABLE, never as an auto-open.
  useEffect(() => {
    // Reset on every itinerary change so stale state never leaks across trips
    setShareEligible(false);
    setShowShareTripModal(false);
    setShareRetry(false);

    if (!p.itinerary) return;
    const it = p.itinerary;
    if (!it.source_plan || !it.end_date) return;
    if (new Date(it.end_date + 'T23:59:59') >= new Date()) return;
    // An UNREADABLE key reads as dismissed, mobile's rule: folding the throw
    // to "not dismissed" opened this modal on every visit for as long as
    // storage stayed broken (internal round 1, note 2).
    let dismissed = true;
    try { dismissed = Boolean(localStorage.getItem(`share_dismissed_${it.id}`)); } catch { /* storage unavailable — never nag */ }
    let cancelled = false;
    checkShareStatus(it.id)
      .then((res) => {
        if (cancelled) return;
        const prompt = completedTripPrompt({
          sourcePlan: it.source_plan, endDate: it.end_date, dismissed,
          shared: Boolean(res.shared), editCount: Number(res.edit_count ?? 0),
          visibility: it.visibility, state: res.publish?.state ?? 'none',
        });
        if (prompt === 'none') return;
        setShareEligible(true);
        setShareRetry(dismissed);
        if (prompt === 'open') setShowShareTripModal(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [p.itinerary]);

  // Fetch AI-generated intel bullets for destination
  useEffect(() => {
    if (!p.itinerary?.destination) return;
    let cancelled = false;
    getIntelBullets(p.itinerary.destination)
      .then(data => { if (!cancelled) setIntelBullets(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [p.itinerary?.destination]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const groupedItems = useMemo(() => p.itinerary ? p.groupItemsByDate() : {}, [p.itinerary]);
  const [selectedDay, setSelectedDay] = React.useState<string | null>(null);
  const dayKeys = useMemo(() => Object.keys(groupedItems), [groupedItems]);

  // Build day list for Move picker in action sheet
  const actionSheetDays = useMemo(() => {
    return Object.keys(groupedItems).sort().map((date, i) => ({
      date,
      dayNum: i + 1,
      label: p.formatShortDate(date),
    }));
  }, [groupedItems, p]);

  // Items to show on map — all items or filtered to selected day
  const mapItems = useMemo(() => {
    if (!selectedDay) return p.itinerary?.items ?? [];
    return groupedItems[selectedDay] ?? [];
  }, [selectedDay, groupedItems, p.itinerary]);

  if (p.isLoading) {
    return (
      <div className="itinerary-page itinerary-v2">
        <div className="itinerary-container">
          <div className="itinerary-loading-skeleton">
            <SkeletonItineraryCard />
            <SkeletonItineraryCard />
            <SkeletonItineraryCard />
          </div>
        </div>
      </div>
    );
  }

  if (p.error && !p.itinerary) {
    return (
      <div className="itinerary-page itinerary-v2">
        <div className="error">{p.error}</div>
        <button onClick={() => p.navigate('/')} className="back-button">
          Back to Home
        </button>
      </div>
    );
  }

  if (!p.itinerary) {
    return (
      <div className="itinerary-page itinerary-v2">
        <div className="error">Itinerary not found</div>
        <button onClick={() => p.navigate('/')} className="back-button">
          Back to Home
        </button>
      </div>
    );
  }

  const itinerary = p.itinerary;


  const isInvitedView = new URLSearchParams(window.location.search).has('invite');
  const isOwner = !isInvitedView && user?.id !== undefined;
  const stat = computeStatLabel(itinerary);
  // Plan decision 5: an upcoming plan says so in a badge, N from the server's
  // `days_until_departure` (the value `computeStatLabel` reads); the meta
  // line's `· In Nd` yields to it. No other lifecycle gets a badge.
  const upcomingDays = itinerary.lifecycle_state === 'upcoming'
    && itinerary.days_until_departure != null && itinerary.days_until_departure > 0
    ? itinerary.days_until_departure : 0;
  const durationDays = itinerary.trip_duration_days
    ?? (itinerary.start_date && itinerary.end_date
      ? Math.round((new Date(itinerary.end_date + 'T00:00:00').getTime() - new Date(itinerary.start_date + 'T00:00:00').getTime()) / 86400000) + 1
      : null);
  const activityCount = itinerary.items.length;

  return (
    <div className="itinerary-page itinerary-v2">
      {/* Hero — destination photo if available, otherwise gradient + emoji */}
      <div className="iv2-hero-wrap">
        {itinerary.hero_image_url ? (
          <>
            <div
              className="iv2-hero-photo"
              style={{ backgroundImage: `url("${itinerary.hero_image_url}")` }}
              role="img"
              aria-label={itinerary.destination ? `${itinerary.destination} hero image` : 'Trip hero image'}
            />
            {/* The terms require the photographer named AND their profile
                linked every time the image is shown. A footer linking only
                unsplash.com does not do that, however prominent it is. */}
            {itinerary.hero_attribution && (
              <UnsplashCredit
                photographer={itinerary.hero_attribution.photographer}
                profileLink={itinerary.hero_attribution.unsplash_link}
                className="iv2-hero-credit"
              />
            )}
          </>
        ) : (
          <div className="iv2-hero-gradient">
            {itinerary.destination
              ? <MapPin size={36} aria-hidden="true" />
              : <Plane size={36} aria-hidden="true" />}
          </div>
        )}
        <div className="iv2-hero-controls">
          <button className="iv2-hero-btn" onClick={() => window.history.length > 1 ? p.navigate(-1) : p.navigate('/')} aria-label="Back">
            <ArrowLeft size={14} />
          </button>
          {isOwner && (
            <button className="iv2-hero-btn" onClick={() => p.setShowMoreMenu(!p.showMoreMenu)} aria-label="More options">
              <MoreVertical size={14} />
            </button>
          )}
        </div>
        {p.showMoreMenu && (
          <>
            <div className="menu-backdrop" onClick={() => p.setShowMoreMenu(false)} />
            <div className="more-menu iv2-hero-menu">
              <button onClick={() => { p.setShowEditModal(true); p.setShowMoreMenu(false); }}>
                Edit Details
              </button>
              <button onClick={() => { p.handleDuplicate(); p.setShowMoreMenu(false); }}>
                Duplicate
              </button>
              <button onClick={() => { p.handleExportPDF(); p.setShowMoreMenu(false); }} disabled={p.isExporting}>
                Download PDF
              </button>
              <div className="menu-divider" />
              {itinerary.lifecycle_state === 'saved' && (
                <button onClick={async () => {
                  try {
                    const updated = await updateItinerary(
                      itinerary.id, {}, itinerary.content_revision ?? 0,
                    );
                    p.setItinerary(updated);
                  } catch { /* silent */ }
                  p.setShowMoreMenu(false);
                }}>
                  Save Plan
                </button>
              )}
              {(itinerary.lifecycle_state === 'upcoming' || itinerary.lifecycle_state === 'in_progress') && (
                <button onClick={async () => {
                  try {
                    // Set end_date to yesterday to mark as completed via lifecycle
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    const updated = await updateItinerary(
                      itinerary.id,
                      { end_date: yesterday.toISOString().split('T')[0] },
                      itinerary.content_revision ?? 0,
                    );
                    p.setItinerary(updated);
                  } catch { /* silent */ }
                  p.setShowMoreMenu(false);
                }}>
                  Mark as Done
                </button>
              )}
              {itinerary.lifecycle_state === 'completed' && (
                <button onClick={async () => {
                  try {
                    const updated = await updateItinerary(
                      itinerary.id, {}, itinerary.content_revision ?? 0,
                    );
                    p.setItinerary(updated);
                  } catch { /* silent */ }
                  p.setShowMoreMenu(false);
                }}>
                  Reactivate
                </button>
              )}
              {/* Lock/Unlock */}
              {itinerary.mode === 'group' && !itinerary.is_locked && (
                <button onClick={async () => {
                  setIsLocking(true);
                  try {
                    const result = await lockPlan(itinerary.id);
                    p.setItinerary(prev => prev ? { ...prev, is_locked: true, locked_at: result.locked_at } : null);
                  } catch { /* silent */ }
                  setIsLocking(false);
                  p.setShowMoreMenu(false);
                }} disabled={isLocking}>
                  Lock plan
                </button>
              )}
              {itinerary.is_locked && (
                <button onClick={async () => {
                  setIsLocking(true);
                  try {
                    await unlockPlan(itinerary.id);
                    p.setItinerary(prev => prev ? { ...prev, is_locked: false, locked_at: null } : null);
                  } catch { /* silent */ }
                  setIsLocking(false);
                  p.setShowMoreMenu(false);
                }} disabled={isLocking}>
                  Unlock plan
                </button>
              )}
              <div className="menu-divider" />
              <button className="danger" onClick={() => { p.setShowDeleteDialog(true); p.setShowMoreMenu(false); }}>
                Delete
              </button>
            </div>
          </>
        )}
      </div>

      {/* Title + meta + CTA (below hero, matches prototype) */}
      <div className="iv2-info">
        <h1 className="iv2-info-title">{getGroupDisplayTitle(itinerary)}</h1>
        <p className="iv2-info-meta">
          {itinerary.destination && <>{itinerary.destination} · </>}
          {durationDays && <>{durationDays} day{durationDays !== 1 ? 's' : ''} · </>}
          {activityCount} activit{activityCount !== 1 ? 'ies' : 'y'}
          {stat.value && !upcomingDays && <> · {stat.label === 'countdown' ? `In ${stat.value}` : stat.label === 'today' ? stat.value : stat.value}</>}
        </p>
        {upcomingDays ? (
          <div className="iv2-info-badge">
            <Badge>{`UPCOMING · IN ${upcomingDays} ${upcomingDays === 1 ? 'DAY' : 'DAYS'}`}</Badge>
          </div>
        ) : null}
        {itinerary.lineage && itinerary.lineage.length > 0 ? (
          <LineageLine lineage={itinerary.lineage} navigate={(to) => p.navigate(to)} />
        ) : itinerary.source_plan ? (
          <p className="iv2-info-forked">Based on a community plan</p>
        ) : null}
        {isOwner && (
          <div className="iv2-info-cta">
            <button className="iv2-cta-primary" onClick={() => {
              planChat.reset({
                intent: 'edit_itinerary',
                entryId: newUuid(),
                itineraryId: itinerary.id,
              });
              setShowChat(true);
            }}>Edit with AI</button>
            <button className="iv2-cta-secondary" onClick={() => setShowShareSheet(true)}>Share</button>
          </div>
        )}
      </div>

      {/* Visiting context */}
      {itinerary.mode === 'visiting' && itinerary.visiting_context && (
        <div className="iv2-visiting-context">
          <span className="iv2-visiting-label">Visiting:</span> {itinerary.visiting_context}
        </div>
      )}

      {/* Lock indicator on title */}
      {itinerary.is_locked && isOwner && (
        <button
          className="iv2-lock-badge iv2-lock-badge--tappable"
          onClick={async () => {
            try {
              await unlockPlan(itinerary.id);
              p.setItinerary(prev => prev ? { ...prev, is_locked: false, locked_at: null } : null);
            } catch { /* silent */ }
          }}
          title="Tap to unlock"
        >
          <span role="img" aria-label="locked">&#128274;</span> Locked · tap to unlock
        </button>
      )}
      {itinerary.is_locked && !isOwner && (
        <div className="iv2-lock-badge" title="Plan is locked by the organizer">
          <span role="img" aria-label="locked">&#128274;</span> Locked
        </div>
      )}

      {/* Invitee strip for group plans */}
      {itinerary.mode === 'group' && (
        <div className="iv2-invitee-strip">
          {(itinerary.invite_count ?? 0) > 0 && (
            <span className="iv2-invitee-count">
              {itinerary.invite_count} invited
            </span>
          )}
          <button
            className="iv2-invite-btn"
            onClick={async () => {
              try {
                const result = await createInvitation(itinerary.id);
                setInviteUrl(result.share_url);
              } catch { /* silent */ }
            }}
          >
            + Invite someone
          </button>
          {inviteUrl && (
            <div className="iv2-invite-link-row">
              <code className="iv2-invite-link-text">{inviteUrl}</code>
              <button
                className="iv2-invite-copy-btn"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(inviteUrl);
                    // Funnel step: plan → shared. Mirrors WhoPicker's
                    // invite_link event so both invite surfaces count alike.
                    trackEvent('plan_shared', {
                      method: 'invite_link',
                      itinerary_id: itinerary.id,
                      plan_slug: itinerary.slug || undefined,
                    });
                    setInviteCopied(true);
                    setTimeout(() => setInviteCopied(false), 2000);
                  } catch { /* */ }
                }}
              >
                {inviteCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Post-trip community share CTA — only when server confirms not already shared */}
      {shareEligible && (
        <div className="itinerary-container">
          <button className="share-trip-prompt__btn" onClick={() => setShowShareTripModal(true)}>
            Share your trip with the community
          </button>
        </div>
      )}

      {/* Utility Row */}
      <div className="iv2-utility-row">
        <div className="iv2-utility-actions">
          <button className="iv2-utility-tile" onClick={() => p.navigate(`/itinerary/${itinerary.id}/packing`)}>
            <span className="iv2-utility-icon">🎒</span>
            <span className="iv2-utility-label">Packing</span>
          </button>
          <button className="iv2-utility-tile" onClick={() => {
            const firstBooking = document.querySelector('.activity-book-link');
            if (firstBooking) firstBooking.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}>
            <span className="iv2-utility-icon">🏨</span>
            <span className="iv2-utility-label">Bookings</span>
          </button>
          <button
            className="iv2-utility-tile"
            onClick={async () => {
              if (exportStatus !== 'idle') return;
              setExportStatus('sending');
              try {
                await exportItineraryEmail(itinerary.id);
                setExportStatus('sent');
                setTimeout(() => setExportStatus('idle'), 3000);
              } catch {
                setExportStatus('idle');
              }
            }}
          >
            <span className="iv2-utility-icon">📅</span>
            <span className="iv2-utility-label">
              {exportStatus === 'sending' ? 'Sending...' : exportStatus === 'sent' ? 'Sent!' : 'Add to Cal'}
            </span>
          </button>
        </div>
      </div>

      {/* Similar Plans inline section */}
      {similarPlans.length > 0 && (
        <div className="iv2-similar-section">
          <div className="iv2-similar-header">
            <span className="iv2-similar-title">Similar plans &middot; {similarPlans.length}</span>
            {similarPlans.length > 3 && (
              <button className="iv2-similar-see-all" onClick={() => p.navigate(`/plans/${itinerary.id}/similar`)}>
                See all {similarPlans.length} &rarr;
              </button>
            )}
          </div>
          <div className="iv2-similar-cards">
            {similarPlans.slice(0, 3).map(plan => {
              const dur = plan.start_date && plan.end_date
                ? Math.round((new Date(plan.end_date + 'T00:00:00').getTime() - new Date(plan.start_date + 'T00:00:00').getTime()) / 86400000) + 1
                : null;
              return (
                <button
                  key={plan.id}
                  className="iv2-similar-card"
                  onClick={() => p.navigate(`/itinerary/${plan.id}`)}
                >
                  <div className="iv2-similar-card-info">
                    <span className="iv2-similar-card-title">{plan.title}</span>
                    <span className="iv2-similar-card-meta">
                      {plan.destination && <>{plan.destination} &middot; </>}
                      {dur && <>{dur}d &middot; </>}
                      {plan.item_count} activit{plan.item_count !== 1 ? 'ies' : 'y'}
                    </span>
                  </div>
                  {plan.lifecycle_state && (
                    <span className={`iv2-similar-badge iv2-similar-badge--${plan.lifecycle_state}`}>
                      {plan.lifecycle_state.replace('_', ' ')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Scrollable content: map + day-by-day */}
      <div className="itinerary-container">
        <div ref={p.printRef} className="printable-content">
          <div className="iv2-pdf-header" aria-hidden>
            <h1 className="iv2-pdf-title">{itinerary.title}</h1>
            <div className="iv2-pdf-meta">
              {itinerary.destination && <span>{itinerary.destination}</span>}
              {itinerary.start_date && (
                <span>{p.formatDateRange(itinerary.start_date, itinerary.end_date)}</span>
              )}
            </div>
          </div>
          {/* Inline Map */}
          <div className="iv2-inline-map" ref={mapRef}>
            <UnifiedMap markers={itineraryItemsToMarkers(mapItems)} />
          </div>
          {/* Day pills — filter map by day */}
          {dayKeys.length > 1 && (
            <div className="iv2-day-pills">
              <button
                className={`iv2-day-pill ${selectedDay === null ? 'active' : ''}`}
                onClick={() => setSelectedDay(null)}
              >
                Overview
              </button>
              {dayKeys.map((date, i) => (
                <button
                  key={date}
                  className={`iv2-day-pill ${selectedDay === date ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedDay(date);
                    // Expand this day, collapse others
                    p.collapseAllDays();
                    p.toggleDayCollapsed(date);
                    mapRef.current?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Day {i + 1}
                </button>
              ))}
            </div>
          )}

          {/* Intel Bullets — AI-generated, free=3 / plus=6 (overview only) */}
          {selectedDay === null && intelBullets && intelBullets.bullets.length > 0 && (
            <div className="iv2-intel-bullets">
              {intelBullets.bullets.map((b, i) => (
                <div key={i} className="iv2-intel-bullet">
                  <span className="iv2-intel-icon">{b.icon || '💡'}</span>
                  <span className="iv2-intel-text">{b.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Route — derived from distinct cities across days (overview only) */}
          {selectedDay === null && (() => {
            // Extract city from address, stripping postal codes and country names
            function extractCity(address: string): string | null {
              const parts = address.split(',').map(s => s.trim()).filter(Boolean);
              if (parts.length < 2) return null;

              // Common countries / regions to skip
              const skipWords = new Set([
                'us','usa','uk','united kingdom','united states','france','germany',
                'italy','spain','japan','canada','australia','mexico','netherlands',
                'england','scotland','wales','ireland',
              ]);

              // Walk from second part looking for a city name
              for (let i = 1; i < parts.length; i++) {
                let part = parts[i];
                // Strip trailing/leading postal codes: "London WC2H 9EU" → "London"
                part = part.replace(/\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/gi, '').trim(); // UK postcodes
                part = part.replace(/\b\d{5}(-\d{4})?\b/g, '').trim(); // US ZIP
                part = part.replace(/\b[A-Z]\d[A-Z]\s*\d[A-Z]\d\b/gi, '').trim(); // CA postal
                part = part.replace(/\b\d{3}-\d{4}\b/g, '').trim(); // JP postal
                part = part.replace(/\b\d{4,6}\b/g, '').trim(); // generic numeric postal
                // Skip if empty after stripping, or is a country, or too short (state codes)
                if (!part) continue;
                if (part.length <= 2) continue;
                if (skipWords.has(part.toLowerCase())) continue;
                return part;
              }
              return null;
            }

            const dayCities: { city: string; dayStart: number; dayEnd: number }[] = [];
            dayKeys.forEach((date, i) => {
              const items = groupedItems[date];
              // Try to find a city from any item's address
              let city: string | null = null;
              for (const it of items) {
                if (it.address) {
                  city = extractCity(it.address);
                  if (city) break;
                }
              }
              if (!city) return;

              const last = dayCities[dayCities.length - 1];
              if (last && last.city.toLowerCase() === city.toLowerCase()) {
                last.dayEnd = i + 1;
              } else {
                dayCities.push({ city, dayStart: i + 1, dayEnd: i + 1 });
              }
            });

            if (dayCities.length < 2) return null;
            return (
              <div className="iv2-route-section">
                <div className="iv2-route-label">Route</div>
                <div className="iv2-route-steps">
                  {dayCities.map((r, i) => (
                    <React.Fragment key={i}>
                      <div className="iv2-route-stop">
                        <div className="iv2-route-city">{r.city}</div>
                        <div className="iv2-route-days">
                          {r.dayStart === r.dayEnd ? `Day ${r.dayStart}` : `Days ${r.dayStart}–${r.dayEnd}`}
                        </div>
                      </div>
                      {i < dayCities.length - 1 && (
                        <div className="iv2-route-connector">
                          <div className="iv2-route-line" />
                          <span className="iv2-route-transport">🚄</span>
                          <div className="iv2-route-line" />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Day-by-day Timeline */}
          <TimelineView
            onActivityPhotoTier={reportPhotoTier}
            groupedItems={groupedItems}
            dayImages={itinerary.day_images}
            selectedDay={selectedDay}
            isEditMode={p.isEditMode}
            setIsEditMode={p.setIsEditMode}
            onEnterEditMode={p.handleEnterEditMode}
            onActivityTap={isOwner ? (item, dayNum) => setActionSheetItem({ item, dayNum }) : undefined}
            onViewDayMap={(date) => { setSelectedDay(date); mapRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
            editingItem={p.editingItem}
            draggedItem={p.draggedItem}
            expandedNotes={p.expandedNotes}
            collapsedDays={p.collapsedDays}
            onToggleDayCollapsed={p.toggleDayCollapsed}
            onCollapseAll={p.collapseAllDays}
            onExpandAll={p.expandAllDays}
            formatShortDate={p.formatShortDate}
            formatTime={p.formatTime}
            sunsetClock={{ effective_timezone: itinerary.effective_timezone }}
            getEventStatus={p.getEventStatus}
            onStartEdit={p.handleStartEdit}
            onEditChange={p.handleEditChange}
            onSaveEdit={p.handleSaveEdit}
            onCancelEdit={p.handleCancelEdit}
            onKeyDown={p.handleKeyDown}
            onAddItem={p.handleAddItem}
            onDeleteItem={p.handleDeleteItem}
            onDragStart={p.handleDragStart}
            onDragOver={p.handleDragOver}
            onDrop={p.handleDrop}
            onToggleNoteExpanded={p.toggleNoteExpanded}
            onCheckin={async (itemId, status) => {
              try {
                await checkinItineraryItem(itinerary.id, itemId, status);
                await p.loadItinerary(itinerary.id);
              } catch { /* silent — circle reverts on refresh */ }
            }}
            onFavorite={async (itemId) => {
              try {
                await favoriteItineraryItem(itinerary.id, itemId);
                await p.loadItinerary(itinerary.id);
              } catch { /* silent */ }
            }}
            lifecycleState={itinerary.lifecycle_state}
            itineraryId={itinerary.id}
            // The owner's controls, and only the owner's. An invitee reads this
            // same view, and every one of these endpoints answers them 404 —
            // after the upload had already asked them who could see a photo
            // they were never going to be allowed to add.
            onPhotoUpload={isOwner ? async (itemId, file, audienceAcknowledged) => {
              const result = await uploadActivityPhoto(itinerary.id, itemId, file, audienceAcknowledged);
              return result.photo_url;
            } : undefined}
            onPhotoDelete={isOwner ? async (itemId) => {
              await deleteActivityPhoto(itinerary.id, itemId);
            } : undefined}
            onPhotoAcknowledgeShare={isOwner ? async (itemId, photoUrl) => {
              try {
                await acknowledgeActivityPhotoShare(itinerary.id, itemId, photoUrl);
              } finally {
                // Reload either way. On success the note leaves once the server
                // has answered; on a 409 the photograph was replaced elsewhere,
                // and the page should show the one that is there now.
                await p.loadItinerary(itinerary.id);
              }
            } : undefined}
            mode={itinerary.mode}
            isLocked={itinerary.is_locked}
            onVote={async (itemId, sentiment) => {
              try {
                await voteItineraryItem(itinerary.id, itemId, sentiment);
                await p.loadItinerary(itinerary.id);
              } catch { /* silent */ }
            }}
          />

          {/* Consolidated Unsplash attribution — the per-day photos and the
              per-activity ones come from the pool, and each thumbnail also
              links to its own photographer.

              The hero clause asks for ATTRIBUTION, not for a URL. A hero is
              either a pool photograph or the plan's own Spaces asset, and the
              URL alone cannot tell them apart — so keying on the URL credited
              Unsplash for photography that was never theirs, which is the
              mirror of the missing credit and just as wrong.

              The activity clause asks what is SHOWN, not what was sent. The API
              ships every rung together and the client picks one, so an item
              carrying `{user_photo_url, pool}` displays the user's own
              photograph — and crediting Unsplash for it would be attributing
              someone's personal photo to a stock library. */}
          {(itinerary.hero_attribution ||
            Object.values(itinerary.day_images || {}).some(Boolean) ||
            anyPoolPhotoShown) && (
            <div className="iv2-unsplash-footer">
              Photos by{' '}
              <a href="https://unsplash.com/?utm_source=vizday&utm_medium=referral" target="_blank" rel="noopener noreferrer">
                Unsplash
              </a>
            </div>
          )}
        </div>

        {p.successMessage && (
          <div className="success-message">{p.successMessage}</div>
        )}

        {p.error && (
          <div className="error-message">
            {p.error}
            <button className="error-dismiss" onClick={p.clearError} aria-label="Dismiss error">&times;</button>
          </div>
        )}

        {/* Delete Activity Confirmation */}
        {p.deleteItemId !== null && (() => {
          const copy = removeStopCopy(itinerary.items.find((i) => i.id === p.deleteItemId));
          return (
            <ConfirmDialog
              isOpen={true}
              title={copy.title}
              message={copy.message}
              confirmText="Remove"
              cancelText="Keep it"
              onConfirm={p.confirmDeleteItem}
              onClose={p.cancelDeleteItem}
              variant="danger"
            />
          );
        })()}

        {/* Delete Itinerary Confirmation Dialog */}
        {p.showDeleteDialog && (
          <DeleteDialog
            itinerary={itinerary}
            onClose={() => p.setShowDeleteDialog(false)}
            onDelete={p.handleDeleteItinerary}
          />
        )}

        {/* Share Modal */}
        {p.showShareModal && p.shareInfo && (
          <ShareModal
            shareUrl={p.shareInfo.share_url.startsWith('http')
              ? p.shareInfo.share_url
              : `${window.location.origin}${p.shareInfo.share_url}`}
            onClose={() => p.setShowShareModal(false)}
            onCopy={p.copyShareLink}
          />
        )}

        {/* Share Trip to Community Modal */}
        {showShareTripModal && (
          <ShareTripModal
            itineraryId={itinerary.id}
            destination={itinerary.destination || ''}
            durationDays={durationDays}
            activityCount={activityCount}
            endDate={itinerary.end_date}
            sourcePlan={itinerary.source_plan}
            visibility={itinerary.visibility}
            retry={shareRetry}
            onClose={() => setShowShareTripModal(false)}
            // The strip goes; the MODAL STAYS, so its success view — the
            // outcome line for the share just made — is on screen at all.
            // Closing here unmounted it in the same commit that set
            // `success`, so the view never painted (internal round 1); its
            // Done button routes through `onClose`, which closes it.
            onShared={() => { setShareEligible(false); }}
          />
        )}

        {/* Edit Modal */}
        {p.showEditModal && itinerary && (
          <ItineraryEditModal
            itinerary={itinerary}
            onClose={() => p.setShowEditModal(false)}
            onSave={(updated) => {
              p.setItinerary(updated);
              p.setShowEditModal(false);
            }}
            onDelete={() => {
              p.navigate('/');
            }}
          />
        )}
      </div>

      {/* Activity Action Sheet */}
      {actionSheetItem && (
        <ActivityActionSheet
          item={actionSheetItem.item}
          dayNum={actionSheetItem.dayNum}
          itineraryId={itinerary.id}
          days={actionSheetDays}
          onClose={() => setActionSheetItem(null)}
          onRemove={(itemId) => { p.handleDeleteItem(itemId); setActionSheetItem(null); }}
          onSwap={async (itemId, suggestion) => {
            try {
              await updateItineraryItem(itinerary.id, itemId, {
                name: suggestion.title,
                location: suggestion.place,
              }, itinerary.content_revision ?? 0);
              await p.loadItinerary(itinerary.id);
              setActionSheetItem(null);
            } catch {
              setActionSheetItem(null);
            }
          }}
          onMove={async (itemId, newDate) => {
            try {
              // Move to new date and set order to end of that day
              const targetDayItems = groupedItems[newDate] || [];
              const newOrder = targetDayItems.length;
              await updateItineraryItem(
                itinerary.id, itemId, { date: newDate, order: newOrder },
                itinerary.content_revision ?? 0,
              );
              await p.loadItinerary(itinerary.id);
              setActionSheetItem(null);
            } catch {
              setActionSheetItem(null);
            }
          }}
          onSaveNote={async (itemId, note) => {
            try {
              await updateItineraryItem(
                itinerary.id, itemId, { notes: note }, itinerary.content_revision ?? 0,
              );
              await p.loadItinerary(itinerary.id);
              setActionSheetItem(null);
            } catch {
              setActionSheetItem(null);
            }
          }}
          onToggleLock={(itemId, confirmed) => toggleItemLock(
            itinerary, itemId, confirmed, {
              apply: (next) => setActionSheetItem(
                prev => applyLockToOpenSheet(prev, itemId, next),
              ),
              reload: p.loadItinerary,
            },
          )}
        />
      )}

      {/* Recap card — completed plans only */}
      {itinerary.lifecycle_state === 'completed' && (
        <div className="iv2-recap-section">
          {itinerary.recap_image_url ? (
            <>
              <img src={itinerary.recap_image_url} alt="Trip recap" className="iv2-recap-image" />
              <div className="iv2-recap-actions">
                <a href={itinerary.recap_image_url} download className="iv2-recap-btn">Download</a>
                <button className="iv2-recap-btn iv2-recap-btn--share" onClick={() => {
                  // Deliberately NOT plan_shared: this sends the recap image
                  // itself, so the recipient has no plan to visit or fork.
                  // Counting it would inflate the plan-distribution metric.
                  const shared = (method: string) => trackEvent('recap_shared', {
                    method,
                    itinerary_id: itinerary.id,
                    plan_slug: itinerary.slug || undefined,
                  });
                  if (navigator.share) {
                    navigator.share({ title: itinerary.title, url: itinerary.recap_image_url })
                      .then(() => shared('native_share'))
                      .catch(() => {}); // user cancelled
                  } else {
                    navigator.clipboard.writeText(itinerary.recap_image_url!)
                      .then(() => shared('copy_link'))
                      .catch(() => {});
                  }
                }}>Share</button>
              </div>
            </>
          ) : (
            <button className="iv2-recap-generate" onClick={async () => {
              await generateRecap(itinerary.id);
              setTimeout(() => p.loadItinerary(itinerary.id), 3000);
            }}>
              Generate trip recap card
            </button>
          )}
        </div>
      )}

      {/* Trip story — completed plans only */}
      {itinerary.lifecycle_state === 'completed' && (
        <div className="iv2-story-section">
          <h3 className="iv2-story-heading">Trip Story</h3>
          {storyLoading ? (
            <div className="iv2-story-loading">
              <span className="iv2-story-spinner" />
              Writing your story...
            </div>
          ) : editingStory ? (
            <textarea
              className="iv2-story-textarea"
              value={storyText}
              onChange={(e) => setStoryText(e.target.value)}
              placeholder="Write your trip story..."
              autoFocus
              onBlur={async () => {
                setEditingStory(false);
                if (storyText !== (itinerary.trip_story || '')) {
                  try {
                    const updated = await saveItineraryContentPatch(
                      itinerary, { trip_story: storyText },
                    );
                    p.setItinerary(updated);
                  } catch { /* silent */ }
                }
              }}
            />
          ) : itinerary.trip_story ? (
            <>
              <div className="iv2-story-display">{itinerary.trip_story}</div>
              <div className="iv2-story-actions">
                <button className="iv2-story-btn" onClick={() => {
                  navigator.clipboard.writeText(itinerary.trip_story || '');
                }}>Copy</button>
                <button className="iv2-story-btn" onClick={() => {
                  setStoryText(itinerary.trip_story || '');
                  setEditingStory(true);
                }}>Edit</button>
                <button className="iv2-story-btn iv2-story-btn--regen" onClick={async () => {
                  setStoryLoading(true);
                  await generateStoryForPlan(itinerary, {
                    apply: (story, content_revision) => p.setItinerary(
                      (prev: typeof itinerary | null) => prev
                        ? { ...prev, trip_story: story, content_revision } : null,
                    ),
                    reload: p.loadItinerary,
                    fail: p.setError,
                  });
                  setStoryLoading(false);
                }}>Regenerate</button>
              </div>
            </>
          ) : (
            <button className="iv2-story-generate" onClick={async () => {
              setStoryLoading(true);
              await generateStoryForPlan(itinerary, {
                apply: (story, content_revision) => p.setItinerary(
                  (prev: typeof itinerary | null) => prev
                    ? { ...prev, trip_story: story, content_revision } : null,
                ),
                reload: p.loadItinerary,
                fail: p.setError,
              });
              setStoryLoading(false);
            }}>
              Write a trip story
            </button>
          )}
        </div>
      )}

      {/* Trip notes — completed plans only */}
      {itinerary.lifecycle_state === 'completed' && (
        <div className="iv2-notes-section">
          <h3 className="iv2-notes-heading">Notes</h3>
          {editingNotes ? (
            <textarea
              className="iv2-notes-textarea"
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              placeholder="What would you do differently?"
              autoFocus
              onBlur={async () => {
                setEditingNotes(false);
                if (notesText !== (itinerary.trip_notes || '')) {
                  try {
                    const updated = await saveItineraryContentPatch(
                      itinerary, { trip_notes: notesText },
                    );
                    p.setItinerary(updated);
                  } catch { /* silent */ }
                }
              }}
            />
          ) : (
            <div
              className={`iv2-notes-display ${!itinerary.trip_notes ? 'iv2-notes-display--empty' : ''}`}
              onClick={() => { setNotesText(itinerary.trip_notes || ''); setEditingNotes(true); }}
            >
              {itinerary.trip_notes || 'Add notes about this trip'}
              <span className="iv2-notes-edit-hint">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Chat overlay */}
      {showChat && (
        <PlanChatOverlay
          chat={planChat}
          isExpanded={showChat}
          chatType="plan_edit"
          planName={getGroupDisplayTitle(itinerary)}
          onClose={() => {
            setShowChat(false);
            void p.loadItinerary(itinerary.id);
          }}
          onNavigate={(path) => p.navigate(path)}
        />
      )}

      {showShareSheet && itinerary && (
        <ShareSheet
          itineraryId={itinerary.id}
          title={itinerary.title}
          destination={itinerary.destination || ''}
          currentVisibility={itinerary.visibility || 'private'}
          currentSlug={itinerary.slug}
          isCompleted={itinerary.lifecycle_state === 'completed'}
          onClose={() => setShowShareSheet(false)}
          onVisibilityChange={(vis, slug) => {
            p.setItinerary(prev => prev ? { ...prev, visibility: vis, slug } : null);
          }}
        />
      )}

      <BottomNav activeItem="plan" />
    </div>
  );
};

export default ItineraryView;
